import { useLayoutEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { type Locale, useLocaleStore } from '@/shared/store/localeStore'

interface LocaleScopeProps {
  locale: Locale
  children: ReactNode
}

/**
 * Ruta con idioma fijo (`/en/*` en inglés; ayuda y legales en español): **la URL manda**.
 *
 * - Aplica su idioma al store (→ i18next) y lo marca como impuesto por la URL
 *   (`enterUrlScope`), para que el idioma de la cuenta (`useLocaleSync`) no lo pise; al salir,
 *   `leaveUrlScope`. No marca `userToggled`: no es una preferencia del usuario.
 * - **No pinta la página hasta que i18next ya está en ese idioma.** Si la pintara antes, la
 *   hija se renderiza en el idioma viejo y, como se suscribe a los cambios de idioma en un
 *   efecto posterior al de este componente, se pierde el aviso y se queda en el idioma viejo
 *   (le pasaba a `/en/about`: llegaba en inglés y se quedaba en español). El cambio ocurre en
 *   un efecto de layout, antes de que el navegador pinte: no hay parpadeo.
 */
export function LocaleScope({ locale, children }: LocaleScopeProps) {
  const { i18n } = useTranslation()
  const [applied, setApplied] = useState(i18n.language === locale)

  useLayoutEffect(() => {
    useLocaleStore.getState().enterUrlScope(locale)
    if (i18n.language === locale) {
      setApplied(true)
    } else {
      setApplied(false)
      void i18n.changeLanguage(locale).then(() => setApplied(true))
    }
    return () => useLocaleStore.getState().leaveUrlScope()
  }, [i18n, locale])

  return applied ? <>{children}</> : null
}
